import DOMPurify from "dompurify";

/**
 * Strips HTML tags from text and converts to plain text
 * @param {string} html - HTML string to convert
 * @returns {string} Plain text without HTML tags
 */
export const stripHtml = (html) => {
  if (!html) return "";

  // Create a temporary DOM element to parse HTML
  const tmp = document.createElement("div");
  tmp.innerHTML = html;

  // Get text content (automatically strips HTML tags)
  return tmp.textContent || tmp.innerText || "";
};

/**
 * Converts Azure DevOps image URLs to use the proxy endpoint
 * @param {string} html - HTML string that may contain Azure DevOps image URLs
 * @param {string|number} connectionId - DevOps connection ID for authentication
 * @returns {string} HTML with proxied image URLs
 */
const convertImageUrlsToProxy = (html, connectionId) => {
  if (!html || !connectionId) return html;

  // Create a temporary DOM element to parse HTML
  const tmp = document.createElement("div");
  tmp.innerHTML = html;

  // Find all img tags
  const images = tmp.querySelectorAll("img");
  images.forEach((img) => {
    const src = img.getAttribute("src");
    if (src) {
      // Skip if already using proxy or data URL
      if (
        src.includes("/api/dashboard/proxy-image") ||
        src.startsWith("data:")
      ) {
        return;
      }

      // Convert Azure DevOps URLs to proxy URLs
      let proxyUrl = null;
      if (
        src.includes("dev.azure.com") &&
        src.includes("/_apis/wit/attachments/")
      ) {
        // Absolute Azure DevOps URL
        proxyUrl = `/api/dashboard/proxy-image?url=${encodeURIComponent(src)}&connection_id=${connectionId}`;
      } else if (
        src.startsWith("/_apis/wit/attachments/") ||
        src.startsWith("_apis/wit/attachments/")
      ) {
        // Relative Azure DevOps URL
        const path = src.startsWith("/") ? src : "/" + src;
        proxyUrl = `/api/dashboard/proxy-image?path=${encodeURIComponent(path)}&connection_id=${connectionId}`;
      }

      if (proxyUrl) {
        // Set proxy URL directly - the browser will make the request
        // We need to ensure the request includes auth, so we'll use a different approach
        // Store the proxy URL and let the browser handle it
        img.setAttribute("src", proxyUrl);
        // Add error handler to log issues
        img.onerror = function () {
          console.error("[Image Proxy] Failed to load image from:", proxyUrl);
        };
      }
    }
  });

  return tmp.innerHTML;
};

/**
 * Sanitizes HTML using DOMPurify and handles images properly
 * @param {string} html - HTML string to sanitize
 * @param {string|number} connectionId - Optional DevOps connection ID for image proxy
 * @returns {string} Sanitized HTML
 */
export const sanitizeHtml = (html, connectionId = null) => {
  if (!html) return "";

  // First, convert Azure DevOps image URLs to use proxy if connectionId is provided
  let processedHtml = html;
  if (connectionId) {
    processedHtml = convertImageUrlsToProxy(html, connectionId);
  }

  // Use DOMPurify to sanitize HTML
  // Allow images, but sanitize dangerous attributes
  const clean = DOMPurify.sanitize(processedHtml, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "s",
      "strike",
      "del",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "blockquote",
      "pre",
      "code",
      "a",
      "img",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "div",
      "span",
      "hr",
    ],
    ALLOWED_ATTR: [
      "href",
      "title",
      "alt",
      "src",
      "width",
      "height",
      "style",
      "class",
      "id",
      "align",
      "colspan",
      "rowspan",
      "border",
      "cellpadding",
      "cellspacing",
    ],
    ALLOW_DATA_ATTR: false,
    // Allow images from any source (Azure DevOps, etc.)
    ALLOW_UNKNOWN_PROTOCOLS: false,
    // Keep relative URLs for images (they might be Azure DevOps attachments)
    KEEP_CONTENT: true,
  });

  return clean;
};
